<template>
  <!-- ✅ Minimal rail (72px) + overlay (off-canvas) sterowany stanem Vue -->
  <aside class="nav-rail d-flex flex-column shadow-sm" aria-label="Admin navigation">
    <!-- przycisk otwierania overlay (chevron) -->
    <button class="rail-btn rail-btn-top" type="button" @click="openOverlay(resolveSectionFromRoute())"
      aria-label="Open menu">
      <vue-feather type="chevron-right" />
    </button>

    <div class="rail-divider" />

    <!-- Ikony (bez etykiet) -->
    <button class="rail-btn" type="button" :class="{ active: isShopActive }" :title="$t('sidebar.shop')"
      :aria-label="$t('sidebar.shop')" @click="$router.push('/shop')">
      <vue-feather type="shopping-bag" />
    </button>

    <button class="rail-btn" type="button" :class="{ active: isProductsGroupActive }" :title="$t('sidebar.products')"
      :aria-label="$t('sidebar.products')" @click="openOverlay('products')">
      <vue-feather type="package" />
    </button>

    <button class="rail-btn" type="button" :class="{ active: isOrdersGroupActive }" :title="$t('sidebar.orders')"
      :aria-label="$t('sidebar.orders')" @click="openOverlay('orders')">
      <vue-feather type="shopping-bag" />
    </button>

    <button class="rail-btn" type="button" :class="{ active: isActiveCartsActive }" :title="$t('sidebar.activeCarts')"
      :aria-label="$t('sidebar.activeCarts')" @click="$router.push('/admin_carts')">
      <vue-feather type="shopping-cart" />
    </button>

    <button v-if="hasRole('ADMIN', 'PARTNER', 'KIEROWNIK')" class="rail-btn" type="button"
      :class="{ active: isShopSettingsGroupActive }" :title="$t('sidebar.shopSettings')"
      :aria-label="$t('sidebar.shopSettings')" @click="openOverlay('shopSettings')">
      <vue-feather type="sliders" />
    </button>

    <button v-if="hasRole('ADMIN')" class="rail-btn" type="button" :class="{ active: isAdminSettingsGroupActive }"
      :title="$t('sidebar.adminSettings')" :aria-label="$t('sidebar.adminSettings')"
      @click="openOverlay('adminSettings')">
      <vue-feather type="shield" />
    </button>

    <div style="position: fixed; bottom: 15px" class="mt-auto">
      <div class="rail-divider" />

      <button  class="rail-btn" type="button" :title="$t('sidebar.logout')" :aria-label="$t('sidebar.logout')"
        @click="logout">
        <vue-feather type="log-out" />
      </button>
    </div>

    <!-- ✅ Overlay -->
    <teleport to="body">
      <div v-if="overlayOpen" class="sv-overlay" role="dialog" aria-modal="true" aria-label="Menu">
        <div class="sv-backdrop" @click="closeOverlay" />

        <div class="sv-panel" :style="{ width: overlayWidth }" @click.stop>
          <div class="sv-panel-header">
            <div class="sv-brand">
              <div class="sv-title">{{ $t('sidebar.title') }}</div>
              <a class="sv-brand-link" href="https://sklep.bestdiamonds.pl/" target="_blank" rel="noreferrer">
                <small>{{ $t('sidebar.brand') }}</small>
              </a>
            </div>

            <button ref="closeBtn" class="sv-close" type="button" @click="closeOverlay" aria-label="Close">
              <vue-feather type="x" />
            </button>
          </div>

          <div class="sv-panel-body">
            <router-link class="nav-link d-flex align-items-center" to="/shop">
              <vue-feather type="shopping-bag" class="me-2" /> {{ $t('sidebar.shop') }}
            </router-link>

            <!-- PRODUKTY (klik w cały wiersz rozwija) -->
            <div class="nav-group">
              <button class="nav-link nav-group-header d-flex align-items-center justify-content-between w-100"
                type="button" @click="toggleProducts" :aria-expanded="productsExpanded ? 'true' : 'false'"
                :aria-label="$t('sidebar.aria.toggleProducts')">
                <span class="d-flex align-items-center">
                  <vue-feather type="package" class="me-2" /> {{ $t('sidebar.products') }}
                </span>
                <vue-feather :type="productsExpanded ? 'chevron-down' : 'chevron-right'" />
              </button>

              <div v-if="productsExpanded" class="submenu ms-4 d-flex flex-column">
                <router-link class="nav-link sub-link d-flex align-items-center" to="/products/client"
                  active-class="ignore-active" exact-active-class="ignore-exact"
                  :class="{ 'nav-active': isProductsTypeActive('') }">
                  <vue-feather type="grid" class="me-2" /> {{ $t('sidebar.productsAll') }}
                </router-link>

                <router-link v-if="hasIndividualProducts" class="nav-link sub-link d-flex align-items-center"
                  :to="{ path: '/products/client', query: { type: 'individual' } }" active-class="ignore-active"
                  exact-active-class="ignore-exact" :class="{ 'nav-active': isProductsTypeActive('individual') }">
                  <vue-feather type="user" class="me-2" /> {{ $t('sidebar.productsIndividual') }}
                </router-link>
              </div>
            </div>

            <!-- ZAMÓWIENIA (klik w cały wiersz rozwija) -->
            <div class="nav-group">
              <button class="nav-link nav-group-header d-flex align-items-center justify-content-between w-100"
                type="button" @click="toggleOrders" :aria-expanded="ordersExpanded ? 'true' : 'false'"
                :aria-label="$t('sidebar.aria.toggleOrders')">
                <span class="d-flex align-items-center">
                  <vue-feather type="shopping-bag" class="me-2" /> {{ $t('sidebar.orders') }}
                </span>
                <vue-feather :type="ordersExpanded ? 'chevron-down' : 'chevron-right'" />
              </button>

              <div v-if="ordersExpanded" class="submenu ms-4 d-flex flex-column">
                <router-link class="nav-link sub-link d-flex align-items-center" to="/orders/pending"
                  active-class="ignore-active" exact-active-class="ignore-exact"
                  :class="{ 'nav-active': isOrdersActive('pending') }">
                  <vue-feather type="clock" class="me-2" /> {{ $t('sidebar.ordersPending') }}
                </router-link>

                <router-link class="nav-link sub-link d-flex align-items-center" to="/orders/placed"
                  active-class="ignore-active" exact-active-class="ignore-exact"
                  :class="{ 'nav-active': isOrdersActive('placed') }">
                  <vue-feather type="send" class="me-2" /> {{ $t('sidebar.ordersPlaced') }}
                </router-link>
              </div>
            </div>

            <router-link class="nav-link d-flex align-items-center" to="/admin_carts">
              <vue-feather type="shopping-cart" class="me-2" /> {{ $t('sidebar.activeCarts') }}
            </router-link>

            <!-- USTAWIENIA SKLEPU (klik w cały wiersz rozwija) -->
            <button v-if="hasRole('ADMIN', 'PARTNER', 'KIEROWNIK')"
              class="nav-link nav-group-header d-flex align-items-center justify-content-between w-100" type="button"
              @click="toggleShopSettings">
              <span class="d-flex align-items-center">
                <vue-feather type="sliders" class="me-2" /> {{ $t('sidebar.shopSettings') }}
              </span>
              <vue-feather :type="shopSettingsExpanded ? 'chevron-down' : 'chevron-right'" />
            </button>

<div v-if="shopSettingsExpanded && hasRole('ADMIN', 'PARTNER', 'KIEROWNIK')" class="submenu ms-4 d-flex flex-column">
  <router-link class="nav-link d-flex align-items-center" to="/settings/client">
    <vue-feather type="layout" class="me-2" /> {{ $t('sidebar.viewSettings') }}
  </router-link>
  <router-link class="nav-link d-flex align-items-center" to="/settings/company">
    <vue-feather type="file-text" class="me-2" /> {{ $t('sidebar.companySettings') }}
  </router-link>
  <router-link class="nav-link d-flex align-items-center" to="/settings/employes">
    <vue-feather type="users" class="me-2" /> {{ $t('sidebar.employees') }}
  </router-link>
</div>

            <!-- USTAWIENIA ADMIN (klik w cały wiersz rozwija) -->
            <button v-if="hasRole('ADMIN')"
              class="nav-link nav-group-header d-flex align-items-center justify-content-between w-100" type="button"
              @click="toggleAdminSettings">
              <span class="d-flex align-items-center">
                <vue-feather type="shield" class="me-2" /> {{ $t('sidebar.adminSettings') }}
              </span>
              <vue-feather :type="adminSettingsExpanded ? 'chevron-down' : 'chevron-right'" />
            </button>

            <div v-if="adminSettingsExpanded && hasRole('ADMIN')" class="ms-4 d-flex flex-column">
              <router-link class="nav-link d-flex align-items-center" to="/admin/catalog-settings">
                <vue-feather type="filter" class="me-2" /> Ustawienia
              </router-link>
              <router-link class="nav-link d-flex align-items-center" to="/settings/users">
                <vue-feather type="user-check" class="me-2" /> {{ $t('sidebar.users') }}
              </router-link>
              <router-link class="nav-link d-flex align-items-center" to="/settings/shops">
                <vue-feather type="briefcase" class="me-2" /> {{ $t('sidebar.contractors') }}
              </router-link>
              <router-link class="nav-link d-flex align-items-center" to="/settings/integrations">
                <vue-feather type="link-2" class="me-2" /> {{ $t('sidebar.apiIntegrations') }}
              </router-link>
              <router-link class="nav-link d-flex align-items-center" to="/settings/integrationsXml">
                <vue-feather type="code" class="me-2" /> {{ $t('sidebar.xmlIntegration') }}
              </router-link>
              <router-link class="nav-link d-flex align-items-center" to="/settings/shippings">
                <vue-feather type="truck" class="me-2" /> {{ $t('sidebar.shippingMethods') }}
              </router-link>
            </div>
          </div>

          <div class="sv-panel-footer">
            <router-link
                class="sv-side-help"
                to="/instructions"
                target="_blank"
                rel="noopener"
            >
                <vue-feather type="help-circle" size="18" />
                <span>{{ $t('sidebar.instructions') }}</span>
            </router-link>

            <div class="p-2">
              <small class="text-muted d-block mb-1">{{ $t('sidebar.language') }}</small>

              <select class="form-select form-select-sm" v-model="selectedLocale" @change="onLocaleChange"
                :disabled="localesLoading">
                <option v-for="opt in localeOptions" :key="opt.value" :value="opt.value">
                  {{ opt.label }}
                </option>
              </select>
            </div>

            <button class="nav-link d-flex align-items-center w-100" type="button" @click="logout">
              <vue-feather type="log-out" class="me-2" /> {{ $t('sidebar.logout') }}
            </button>

            <small v-if="userEmail" class="text-muted ms-2 d-block pb-2">
              {{ userEmail }}
            </small>
          </div>
        </div>
      </div>
    </teleport>
  </aside>
</template>

<script>
import VueFeather from 'vue-feather'
import { applyLocaleForView, fetchLocaleOptions, FALLBACK, loadViewLocale, toBCP47 } from '@/i18n/index.js'
import { useSettingsStore } from '@/stores/settings'
import { useAuthStore } from '@/stores/auth'

export default {
  name: 'Sidebar',
  components: { VueFeather },
  data() {
    return {
      overlayOpen: false,
      overlayWidth: '320px',
      lastOverlaySection: null,

      productsExpanded: true,
      ordersExpanded: true,
      shopSettingsExpanded: true,
      adminSettingsExpanded: false,

      selectedLocale: localStorage.getItem('sv-lang') || FALLBACK,
      localeOptions: [],
      localesLoading: false
    }
  },
  computed: {
    userEmail() {
      return useAuthStore().user?.email || ''
    },
    hasIndividualProducts() {
      const flag = localStorage.getItem('hasIndividualProducts-B2B')
      if (flag === 'true') return true
      if (flag === 'false') return false
      return false
    },

    // --- aktywne stany w railu ---
    isShopActive() {
      const p = this.$route?.path || ''
      return p.startsWith('/shop')
    },
    isProductsGroupActive() {
      return (this.$route?.path || '').startsWith('/products/client')
    },
    isOrdersGroupActive() {
      return (this.$route?.path || '').startsWith('/orders')
    },
    isActiveCartsActive() {
      return (this.$route?.path || '').startsWith('/admin_carts')
    },
    isShopSettingsGroupActive() {
      const p = this.$route?.path || ''
      return (
        p.startsWith('/settings/client') ||
        p.startsWith('/settings/company') ||
        p.startsWith('/settings/employes')
      )
    },
    isAdminSettingsGroupActive() {
      const p = this.$route?.path || ''
      return (
        p.startsWith('/admin/catalog-settings') ||
        p.startsWith('/settings/users') ||
        p.startsWith('/settings/shops') ||
        p.startsWith('/settings/integrations') ||
        p.startsWith('/settings/integrationsXml') ||
        p.startsWith('/settings/shippings')
      )
    }
  },
  async mounted() {
    await applyLocaleForView('Sidebar', this.selectedLocale)

    this.localesLoading = true
    try {
      const opts = await fetchLocaleOptions()
      this.localeOptions = Array.isArray(opts) ? opts : []
    } finally {
      this.localesLoading = false
    }

    const current = toBCP47(this.selectedLocale)
    if (!this.localeOptions.some(o => o.value === current)) {
      this.localeOptions.unshift({ value: current, label: current })
    }
    this.selectedLocale = current

    window.addEventListener('keydown', this.onWindowKeydown)
  },
  beforeUnmount() {
    this.unlockBodyScroll()
    window.removeEventListener('keydown', this.onWindowKeydown)
  },
  methods: {
    onWindowKeydown(e) {
      if (!this.overlayOpen) return
      if (e.key === 'Escape') this.closeOverlay()
    },

    lockBodyScroll() {
      try { document.body.classList.add('sv-overlay-open') } catch { }
    },
    unlockBodyScroll() {
      try { document.body.classList.remove('sv-overlay-open') } catch { }
    },

    openOverlay(section = null) {
      this.overlayOpen = true
      this.lockBodyScroll()

      const target = section || this.lastOverlaySection
      if (target) this.expandOnly(target)
      this.lastOverlaySection = target || this.lastOverlaySection

      this.$nextTick(() => {
        try { this.$refs.closeBtn?.focus?.() } catch { }
      })
    },

    closeOverlay() {
      this.overlayOpen = false
      this.unlockBodyScroll()
    },

    expandOnly(section) {
      this.productsExpanded = section === 'products'
      this.ordersExpanded = section === 'orders'
      this.shopSettingsExpanded = section === 'shopSettings'
      this.adminSettingsExpanded = section === 'adminSettings'
    },

    resolveSectionFromRoute() {
      const p = this.$route?.path || ''
      if (p.startsWith('/products/client')) return 'products'
      if (p.startsWith('/orders')) return 'orders'
      if (p.startsWith('/settings/client') || p.startsWith('/settings/company') || p.startsWith('/settings/employes')) {
        return 'shopSettings'
      }
      if (
        p.startsWith('/admin/catalog-settings') ||
        p.startsWith('/settings/users') ||
        p.startsWith('/settings/shops') ||
        p.startsWith('/settings/integrations') ||
        p.startsWith('/settings/integrationsXml') ||
        p.startsWith('/settings/shippings')
      ) {
        return 'adminSettings'
      }
      return this.lastOverlaySection
    },

    toggleProducts() { this.productsExpanded = !this.productsExpanded },
    toggleOrders() { this.ordersExpanded = !this.ordersExpanded },
    toggleShopSettings() { this.shopSettingsExpanded = !this.shopSettingsExpanded },
    toggleAdminSettings() { this.adminSettingsExpanded = !this.adminSettingsExpanded },

    async onLocaleChange() {
      const settings = useSettingsStore()
      await settings.changeLocale(toBCP47(this.selectedLocale))
      await loadViewLocale('Sidebar', settings.locale)
      this.selectedLocale = settings.locale

      try {
        window.dispatchEvent(new CustomEvent('sv:locale-changed', { detail: { locale: settings.locale } }))
      } catch { }

      this.$router.go(0)
    },

    isOrdersActive(kind) {
      const p = this.$route?.path || ''
      if (!p.startsWith('/orders')) return false
      if (kind === 'pending') return p === '/orders/pending' || p === '/orders'
      if (kind === 'placed') return p === '/orders/placed'
      return false
    },

    isProductsTypeActive(expected) {
      if (!this.$route?.path?.startsWith('/products/client')) return false
      const current = (this.$route.query?.type || '')
      return String(current) === String(expected || '')
    },

    async logout() {
      const auth = useAuthStore()
      await auth.logout()
      localStorage.removeItem('hasIndividualProducts-B2B')
      this.closeOverlay()
      this.$router.push('/login')
    },

    hasRole(...roles) {
      return useAuthStore().hasRole(...roles)
    },
  }
}
</script>

<style scoped>
/* =========================
   RAIL (72px)
   ========================= */

.nav-rail {
  width: 72px;
  min-width: 72px;
  background-color: #ffffff;
  border-right: 1px solid #b9bbbd;
  min-height: 100vh;
  padding: 10px 8px;
  gap: 6px;
  -webkit-tap-highlight-color: transparent;
}

.rail-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
  margin: 6px 0;
}

.rail-btn {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #141824;
}

.rail-btn-top {
  height: 44px;
  border-radius: 12px;
}

.rail-btn.active {
  background: #141824;
  color: #ffffff;
}

/* hover tylko dla myszy/trackpada (żeby na dotyku nie "przyklejało") */
@media (hover: hover) and (pointer: fine) {
  .rail-btn:hover {
    background: rgba(0, 0, 0, 0.06);
  }
}

/* =========================
   OVERLAY
   ========================= */

:global(body.sv-overlay-open) {
  overflow: hidden;
}

:global(.sv-overlay) {
  position: fixed;
  inset: 0;
  z-index: 2000;
}

:global(.sv-backdrop) {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
}

:global(.sv-panel) {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: #ffffff;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  animation: sv-slide-in 160ms ease-out;
}

@keyframes sv-slide-in {
  from {
    transform: translateX(-16px);
    opacity: 0.6;
  }

  to {
    transform: translateX(0);
    opacity: 1;
  }
}

:global(.sv-panel-header) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

:global(.sv-brand) {
  min-width: 0;
}

:global(.sv-title) {
  font-weight: 700;
  font-size: 15px;
  line-height: 1.2;
}

:global(.sv-brand-link) {
  text-decoration: none;
  color: inherit;
}

:global(.sv-close) {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

@media (hover: hover) and (pointer: fine) {
  :global(.sv-close:hover) {
    background: rgba(0, 0, 0, 0.06);
  }
}

:global(.sv-panel-body) {
  flex: 1;
  padding: 8px 10px;
}

:global(.sv-panel-footer) {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

/* =========================
   LINKI / GRUPY
   ========================= */

.nav-link {
  cursor: pointer;
  text-decoration: none;
  padding: 1rem;
  display: flex;
  align-items: center;
  margin: 1px;
  border-radius: 5px;
  color: #000000;
  background: transparent;
  border: 0;
  width: auto;
  -webkit-tap-highlight-color: transparent;
}

.nav-link.router-link-exact-active {
  color: #ffffff !important;
  background-color: black;
  font-weight: 600;
}

@media (hover: hover) and (pointer: fine) {
  .nav-link:hover {
    color: #ffffff !important;
    background-color: black;
    font-weight: 600;
  }
}

.nav-link:active {
  background: rgba(0, 0, 0, 0.06);
}

.nav-group {
  display: flex;
  flex-direction: column;
}

.nav-group-header {
  text-align: left;
}

.submenu {
  border-left: 2px solid rgba(0, 0, 0, 0.08);
  padding-left: 8px;
  margin-top: 2px;
  margin-bottom: 2px;
}

.sub-link {
  padding: 0.5rem 0.5rem;
  font-size: 13px;
  opacity: 0.95;
}

a {
  color: #000000;
}

.nav-link.nav-active {
  color: #ffffff !important;
  background-color: black;
  font-weight: 600;
}

.sv-side-help {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 12px 14px;
    border-radius: 10px;
    color: #111;
    text-decoration: none;
    font-weight: 800;
    box-sizing: border-box;
}

.sv-side-help:hover {
    background: #f1f1f1;
}

.sv-side-help svg {
    flex: 0 0 auto;
}
</style>
